import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Users, MapPin, ExternalLink, Check, X, Building2, Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import { safeFetchJson } from "@/utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event type badges with colors
const EVENT_TYPE_STYLES = {
  audition: { bg: "bg-purple-100", text: "text-purple-800", label: "Audition" },
  meeting: { bg: "bg-blue-100", text: "text-blue-800", label: "Meeting" },
  callback: { bg: "bg-orange-100", text: "text-orange-800", label: "Callback" },
  workshop: { bg: "bg-green-100", text: "text-green-800", label: "Workshop" },
  live_class: { bg: "bg-pink-100", text: "text-pink-800", label: "Live Class" }
};

// Participant status badges
const STATUS_STYLES = {
  invited: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Invited" },
  accepted: { bg: "bg-green-100", text: "text-green-800", label: "Accepted" },
  declined: { bg: "bg-red-100", text: "text-red-800", label: "Declined" },
  attended: { bg: "bg-blue-100", text: "text-blue-800", label: "Attended" }
};

export default function EventCard({ 
  event, 
  viewType = "participant", // "participant" | "host" | "public"
  onAccept,
  onDecline,
  onJoin,
  onEdit,
  onCancel,
  onRegister,
  onViewParticipants,  // New prop for viewing participants
  compact = false
}) {
  const [loading, setLoading] = useState(false);
  
  const eventType = EVENT_TYPE_STYLES[event.event_type] || EVENT_TYPE_STYLES.meeting;
  const participationStatus = event.my_participation?.status;
  const statusStyle = participationStatus ? STATUS_STYLES[participationStatus] : null;
  
  // Format date and time
  const eventDate = new Date(event.start_at);
  const dateStr = eventDate.toLocaleDateString("en-US", { 
    weekday: "short", 
    month: "short", 
    day: "numeric",
    year: "numeric"
  });
  const timeStr = eventDate.toLocaleTimeString("en-US", { 
    hour: "numeric", 
    minute: "2-digit",
    hour12: true 
  });
  
  const handleAccept = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const parsed = await safeFetchJson(`${API_URL}/api/events/${event.event_id}/accept`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!parsed.ok) {
        throw new Error(parsed.data?.detail || "Failed to accept invitation");
      }
      
      toast.success("Invitation accepted!");
      onAccept?.(parsed.data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDecline = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const parsed = await safeFetchJson(`${API_URL}/api/events/${event.event_id}/decline`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!parsed.ok) {
        throw new Error(parsed.data?.detail || "Failed to decline invitation");
      }
      
      toast.success("Invitation declined");
      onDecline?.(parsed.data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRegister = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const parsed = await safeFetchJson(`${API_URL}/api/events/${event.event_id}/register`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!parsed.ok) {
        throw new Error(parsed.data?.detail || "Failed to register for event");
      }
      
      toast.success("Successfully registered!");
      onRegister?.(parsed.data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleJoin = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (event.meeting_link) {
      window.open(event.meeting_link, "_blank", "noopener,noreferrer");
    } else {
      toast.error("Meeting link not available. Please accept the invitation first.");
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const shareUrl = `${window.location.origin}/events/public/${event.event_id}`;
    const shareText = `Check out this event: ${event.title} on TalentMeet`;
    
    // Try native share first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text: shareText,
          url: shareUrl
        });
        return;
      } catch (err) {
        // User cancelled or error, fall back to copy
      }
    }
    
    // Fall back to copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Event link copied to clipboard!");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("Event link copied!");
    }
  };

  return (
    <div 
      className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow ${compact ? "p-4" : "p-5"}`}
      data-testid={`event-card-${event.event_id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Badge className={`${eventType.bg} ${eventType.text} font-medium`}>
              {eventType.label}
            </Badge>
            {event.visibility_scope === "public" && (
              <Badge variant="outline" className="text-xs">Public</Badge>
            )}
            {statusStyle && viewType === "participant" && (
              <Badge className={`${statusStyle.bg} ${statusStyle.text}`}>
                {statusStyle.label}
              </Badge>
            )}
            {event.status === "cancelled" && (
              <Badge className="bg-red-100 text-red-800">Cancelled</Badge>
            )}
          </div>
          <h3 className={`font-semibold text-gray-900 ${compact ? "text-base" : "text-lg"} line-clamp-2`}>
            {event.title}
          </h3>
        </div>
      </div>
      
      {/* Host Info */}
      {event.host_info && (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
          <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center">
            <span className="text-xs font-medium text-violet-600">
              {(event.host_info.display_name || event.host_info.name)?.[0]?.toUpperCase()}
            </span>
          </div>
          <span>Hosted by <span className="font-medium">{event.host_info.display_name || event.host_info.name}</span></span>
        </div>
      )}
      
      {/* Partner Enterprise */}
      {event.partner_enterprise_info && (
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
          <Building2 className="w-4 h-4" />
          <span>In collaboration with <span className="font-medium text-gray-700">{event.partner_enterprise_info.display_name}</span></span>
        </div>
      )}
      
      {/* Description */}
      {event.description && !compact && (
        <p className="mt-3 text-sm text-gray-600 line-clamp-2">
          {event.description}
        </p>
      )}
      
      {/* Date, Time, Duration */}
      <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span>{dateStr}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-gray-400" />
          <span>{timeStr} ({event.duration_minutes} min)</span>
        </div>
        {viewType === "host" && (
          <div className="flex items-center gap-1.5">
            <Users className="w-4 h-4 text-gray-400" />
            <span>{event.accepted_count || 0}/{event.participant_count || 0} accepted</span>
          </div>
        )}
        {viewType === "public" && event.participant_count > 0 && (
          <div className="flex items-center gap-1.5">
            <Users className="w-4 h-4 text-gray-400" />
            <span>{event.participant_count} registered</span>
          </div>
        )}
      </div>
      
      {/* Facilitators */}
      {event.facilitators && event.facilitators.length > 0 && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <span className="text-gray-500">Facilitators:</span>
          <div className="flex flex-wrap gap-1">
            {event.facilitators.map(fac => (
              <span key={fac.trainer_user_id} className="text-gray-700 font-medium">
                {fac.trainer_info?.display_name || "Trainer"}
                {fac.role_label === "lead" && " (Lead)"}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Actions */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 flex-wrap">
        {/* Participant Actions */}
        {viewType === "participant" && (
          <>
            {participationStatus === "invited" && (
              <>
                <Button 
                  size="sm" 
                  onClick={handleAccept}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="accept-event-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Accept
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleDecline}
                  disabled={loading}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="decline-event-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Decline
                </Button>
              </>
            )}
            {participationStatus === "accepted" && event.meeting_link && (
              <Button 
                size="sm" 
                onClick={handleJoin}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="join-event-btn"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Join Meeting
              </Button>
            )}
            {participationStatus === "declined" && (
              <span className="text-sm text-gray-500">You declined this invitation</span>
            )}
            {/* Share button for participants */}
            <Button 
              size="sm" 
              variant="ghost"
              onClick={handleShare}
              className="text-gray-500 hover:text-violet-600"
              data-testid="share-event-btn"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </>
        )}
        
        {/* Host Actions */}
        {viewType === "host" && (
          <>
            {event.meeting_link && (
              <Button 
                size="sm" 
                onClick={handleJoin}
                className="bg-violet-600 hover:bg-violet-700"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Join
              </Button>
            )}
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => onViewParticipants?.(event)}
              data-testid="view-participants-btn"
            >
              <Users className="w-4 h-4 mr-1" />
              {event.participant_count || 0}
            </Button>
            {/* Share button for hosts */}
            <Button 
              size="sm" 
              variant="outline"
              onClick={handleShare}
              data-testid="share-event-host-btn"
            >
              <Share2 className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => onEdit?.(event)}
            >
              Edit
            </Button>
            {event.status === "published" && (
              <Button 
                size="sm" 
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => onCancel?.(event)}
              >
                Cancel Event
              </Button>
            )}
          </>
        )}
        
        {/* Public Actions */}
        {viewType === "public" && (
          <>
            <Button 
              size="sm" 
              onClick={handleRegister}
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="register-event-btn"
            >
              Register
            </Button>
            {/* Share button for public view */}
            <Button 
              size="sm" 
              variant="ghost"
              onClick={handleShare}
              className="text-gray-500 hover:text-violet-600"
              data-testid="share-event-public-btn"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
